<template>
    <Form>
        <FormComponent :hasTopPadding="false">
            <TextInput
                label="Reference"
                placeholder="Enter a reference (e.g: ABC123) or leave blank to autogenerate."
                :isEditing="isEditing"
                :value="reference"
                :errors="referenceError"
                :expand="true"
                :null-text="`${invoice?.id} (auto-generated)`"
                @update:value="(value: string) => (reference = value)"
            />
        </FormComponent>

        <FormComponent>
            <DropdownSelector
                id="invoice-customer"
                label="Customer"
                :value="customer"
                :errors="customerError"
                :isEditing="isEditing && customerEditable"
                :selectors="allCustomerSelections"
                :required="true"
                @update:value="(value: number) => (customer = value)"
            />
        </FormComponent>

        <FormComponent>
            <RadioButtonGroup
                label="Payment Type"
                id="invoice-payment-type"
                name="invoice-payment-type"
                :value="paymentType"
                :required="true"
                :isEditing="isEditing"
                :selections="paymentOptions"
                :errors="paymentTypeError"
                @update:value="(v: string) => (paymentType = v)"
            />
        </FormComponent>

        <FormComponent>
            <DateTimePicker
                label="Issue Date"
                id="invoice-issue-date"
                name="invoice-issue-date"
                mode="date"
                timezone="utc"
                :value="issueDate"
                :isEditing="isEditing"
                :errors="issueDateError"
                :displayValue="prettifyInvoiceDate(issueDate)"
                @update:value="(v: string) => (issueDate = v)"
            />
        </FormComponent>

        <FormComponent>
            <DateTimePicker
                label="Expected Payment Date"
                id="invoice-expected-payment-date"
                name="invoice-expected-payment-date"
                mode="date"
                timezone="utc"
                :value="expectedPaymentDate"
                :isEditing="isEditing"
                :errors="expectedPaymentDateError"
                :displayValue="prettifyInvoiceDate(expectedPaymentDate)"
                @update:value="(v: string) => (expectedPaymentDate = v)"
            />
        </FormComponent>

        <FormComponent>
            <DateTimePicker
                label="Payment Date"
                id="invoice-payment-date"
                name="invoice-payment-date"
                mode="date"
                timezone="utc"
                :value="paymentDate"
                :isEditing="isEditing"
                :errors="paymentDateError"
                :displayValue="prettifyInvoiceDate(paymentDate)"
                @update:value="(v: string) => (paymentDate = v)"
            />
        </FormComponent>

        <div class="grid grid-flow-row grid-cols-3 md:grid-cols-3 grid-rows-2 pt-5 items-start" v-if="invoice?.status === InvoiceStatus.SENT">
            <div class="col-span-1 block font-medium text-gray-700 dark:text-gray-100">
                Reminders Sent
            </div>
            <div class="col-span-2">
                <div class="flex flex-row gap-2 dark:text-gray-100">

                    <span class="font-bold">{{ invoice.reminders_sent }}</span>
                    <span
                        v-if="invoice.reminders_sent > 0 && invoice.reminded_at"
                        class="text-xs self-center">
                    (last reminder sent: {{ prettifyInvoiceDate(invoice.reminded_at) }})
                </span>
                </div>
            </div>
            <div class="col-span-1"></div>
            <div class="col-span-2 md:col-span-2 -mt-2 dark:text-gray-100">
                <Button
                    type="button"
                    :disabled="!canSendReminder()"
                    variety="primary"
                    @click.prevent="onRequestReminder"
                >
                    Send Reminder
                </Button>

                <div class="text-xs text-gray-400"><span class="font-bold">Note: </span>you are permitted to send a single reminder per day.</div>
            </div>
        </div>

        <template
            #buttons
            v-if="showButtons"
        >
            <Button
                variety="plain"
                @click="$emit('onCancel')"
            >
                Cancel
            </Button>
            <Button
                variety="primary"
                @click="onSubmit"
            >
                Save
            </Button>
        </template>
    </Form>
</template>

<script lang="ts">
import {defineComponent, computed, PropType, watch, onErrorCaptured, inject} from 'vue'
import * as yup from "yup";
import { useForm, useField } from "vee-validate";
import useCustomerStore from "../../customer/useCustomerStore";
import useMiscInformationStore from '../../../utils/useMiscInformationStore';
import Invoice from '../types/Invoice';
import InvoiceStatus from '../types/InvoiceStatus';
import PaymentType from '../types/PaymentType';
import moment from 'moment';
import Button from "../../../components/forms/Button.vue";

export default defineComponent({
    components: {Button},
    props: {
        isEditing: {
            type: Boolean,
            required: true
        },
        customerEditable: {
            type: Boolean,
            required: true,
        },
        showButtons: {
            type: Boolean,
            required: false,
            default: true
        },
        invoice: {
            type: Object as PropType<Invoice>,
            required: false
        }
    },
    emits:[
        'onSubmit',
        'onCancel',
        'requestReminder'
    ],
    setup(props, context) {
        const customerStore = useCustomerStore();
        const miscInformationStore = useMiscInformationStore();

        const paymentOptions = computed(() => miscInformationStore.paymentTypes);
        const allCustomerSelections = computed(
            () => customerStore.availableCustomers.map(customer => ({ name: customer.name, value: customer.id }))
        );

          // Form Validation Schema
        const schema = yup.object({
            customer_id: yup.number().required("Customer is required."),
            reference: yup.string().nullable(),
            payment_type: yup.string().required("Payment type is required."),
            expected_payment_date: yup.date().nullable(),
            issue_date: yup.date().nullable(),
            payment_date: yup.date().nullable()
        });

        // Form context
        const {handleSubmit, resetForm} = useForm({
            validationSchema: schema,
        });

        // form fields
        const { value: customer, errorMessage: customerError } = useField<number>("customer_id");
        const { value: reference, errorMessage: referenceError } = useField<string>("reference");
        const { value: paymentType, errorMessage: paymentTypeError } = useField<string>("payment_type");
        const { value: expectedPaymentDate, errorMessage: expectedPaymentDateError } = useField<string>("expected_payment_date");
        const { value: issueDate, errorMessage: issueDateError } = useField<string>("issue_date");
        const { value: paymentDate, errorMessage: paymentDateError } = useField<string>("payment_date");

        watch(
            props,
            () => {
                resetForm();
                if (props.invoice) {
                    customer.value = props.invoice.customer_id;
                    paymentType.value = props.invoice.payment_type;

                    if (props.invoice.expected_payment_date) {
                        expectedPaymentDate.value = props.invoice.expected_payment_date;
                    }
                    if (props.invoice.reference) {
                        reference.value = props.invoice.reference;
                    }
                    if (props.invoice.issue_date) {
                        issueDate.value = props.invoice.issue_date;
                    }

                    if (props.invoice.payment_date) {
                        paymentDate.value = props.invoice.payment_date;
                    }
                } else {
                    paymentType.value = PaymentType.DIRECT_DEBIT;
                }
            },
            { immediate: true }
        );

        const onSubmit = handleSubmit(values => {
            context.emit('onSubmit', values);
        });

        const onRequestReminder = () => {
            context.emit('requestReminder');
        }

        function prettifyInvoiceDate(date: string): string | undefined {
            if (!date) {
                return undefined;
            }

            return moment(date).format("DD/MM/yyyy");
        }

        const canSendReminder = (): boolean => {
            if(props.invoice?.reminded_at) {
                return prettifyInvoiceDate(props.invoice.reminded_at) !== moment().format("DD/MM/yyyy")
            }
            return false;
        }

        return {
            InvoiceStatus,
            paymentOptions,
            allCustomerSelections,

            customer,
            customerError,

            reference,
            referenceError,

            paymentType,
            paymentTypeError,

            expectedPaymentDate,
            expectedPaymentDateError,

            issueDate,
            issueDateError,

            paymentDate,
            paymentDateError,

            prettifyInvoiceDate,
            onSubmit,
            onRequestReminder,
            canSendReminder
        }
    },
})
</script>
